import {
  CannedAnswerChannelEnum,
  IChatConfig,
  IChatSessionStatus,
  ISharedFullConversation,
  TConversationChatSession,
  Undefinable,
} from "atlas-shared";
import { Button, Modal } from "antd";
import { useState, useRef, useEffect } from "react";
import "./conversation.chat_form.scss";
import { useTranslation } from "react-i18next";
import { useCannedAnswers, useChatSession } from "@Hooks";
import { tryCatch } from "@Utils";
import { endChatSession } from "@Api";
import { EditorFroala, IEditorFroalaRef } from "@Components/editor-froala";
import { hashOptions, parseHashOptions, htmlToPlainTextWithLineBreaks } from "@Utils";

interface IProps {
  conversationId: number;
  conversation: Undefinable<ISharedFullConversation>;
  activeChatSession?: TConversationChatSession;
  cb?(): void;
}

export const ConversationChatForm = ({ conversationId, conversation, activeChatSession, cb }: IProps) => {
  const [isSending, setIsSending] = useState(false);
  const { t } = useTranslation();
  const { connectChatSession, sendMessage, sessionStatus, handleTypingStart, handleTypingStop } =
    useChatSession(conversationId, conversation);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const cannedAnswers = useCannedAnswers();
  const editorRef = useRef<IEditorFroalaRef>(null);
  const chatCannedAnswers = cannedAnswers.canned_answers.filter(
    (cannedAnswer) =>
      cannedAnswer.channel === CannedAnswerChannelEnum.Chat && !cannedAnswer.deleted_at
  );

  const currentStatus = sessionStatus ?? activeChatSession?.status;
  const isSessionActive = currentStatus === IChatSessionStatus.ACTIVE;

  useEffect(() => {
    if (activeChatSession && isSessionActive) {
      connectChatSession(activeChatSession.chatConfigId, activeChatSession.sessionId);
    }
  }, [activeChatSession, isSessionActive, connectChatSession]);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = async () => {
    setIsModalOpen(false);
    await endChatSession(activeChatSession?.sessionId || "");
    if (cb) cb();
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const send = async (messageToSend: string) => {
    const cleanMessage = htmlToPlainTextWithLineBreaks(messageToSend).trim();

    if (isSending || !cleanMessage || !activeChatSession || !isSessionActive) return;

    const parsedMessage = parseHashOptions(cleanMessage);

    handleTypingStop();
    setIsSending(true);
    const { data, error } = await tryCatch(
      sendMessage(
        parsedMessage,
        { id: activeChatSession.chatConfigId } as IChatConfig,
        activeChatSession
      )
    );

    if (error) console.error(error);

    setIsSending(false);
  };

  const handleSendButtonClick = () => {
    if (editorRef.current) {
      const currentContent = editorRef.current.getContent();
      if (currentContent && currentContent.trim()) {
        send(currentContent);
        editorRef.current.clearContent();
      }
    }
  };

  if (!activeChatSession || !isSessionActive) {
    return null;
  }

  return (
    <div className="conversation-chat-form">
      <EditorFroala
        ref={editorRef}
        disableRichText={true}
        onSend={send}
        onChangeTrigger={(value) => {
          if (value && value.trim()) {
            handleTypingStart();
          }
        }}
        onBlur={() => {
          handleTypingStop();
        }}
        onTypingStart={handleTypingStart}
        onTypingStop={handleTypingStop}
        editorToggle={null}
        canned_answers={chatCannedAnswers}
        hashOptions={hashOptions}
        isCannedAnswersLoaded={cannedAnswers.loaded}
        sendOnEnter={true}
      />
      <div style={{ position: "relative", bottom: "42px", zIndex: 10 }}>
        <Button type="primary" className="message-send" onClick={handleSendButtonClick}>
          {t("SEND")}
        </Button>
        <Button type="default" className="message-close" onClick={showModal}>
          {t("END_CHAT")}
        </Button>
      </div>
      <Modal
        cancelText={t("CANCEL")}
        okText={t("END_CHAT")}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <h2>{t("END_CHAT_SESSION")}</h2>
        <br></br>
        <p>{t("END_CHAT_SESSION_CONFIRMATION")}</p>
      </Modal>
    </div>
  );
};
